import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import proDocumentsService from '../../../services/proDocuments.service';
import type {
  ProDocumentRow,
  ProDocumentUpdate,
  ProDocumentFilters,
} from '../../../types/proDocuments';
import DocsTable from '../components/docs/DocsTable';
import DocsUploadForm from '../components/docs/DocsUploadForm';
import DocsFilterBar from '../components/docs/DocsFilterBar';
import DocsEditDialog from '../components/docs/DocsEditDialog';
import DocsHistoryDrawer from '../components/docs/DocsHistoryDrawer';
import DocPreviewLightbox from '../components/docs/DocPreviewLightbox';
import { useChangeAll } from '../changeAll/ChangeAllContext';
import ChangeAllReviewModal from '../changeAll/ChangeAllReviewModal';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';
import { TrashIcon } from '@heroicons/react/24/outline';
import PermissionsContainer from '../../../components/PermissionsContainer';
import { useTryPermissionsMode } from '../../../contexts/PermissionsModeContext';
import {
  INVOICE_DOC_DELETE_PERMISSION_ID,
  INVOICE_DOC_DELETE_PERMISSION_NAME,
} from '../components/docs/DocsTable';

interface DocsTabProps {
  /** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110"). */
  pronumber: number | string;
  /**
   * The order's editing hook. When present the Invoice (forinvoice) column STAGES into
   * it and commits with the order Save, which is V1's behaviour (the grid ships every
   * dropdown with the order via dosaverecord's `documentlist` prefix; nothing writes
   * this field on its own). Optional so the tab still renders standalone — without it
   * the column falls back to the immediate write.
   */
  editing?: {
    docInvoiceFlagsDraft: Record<number, 'Y' | 'N'>;
    setDocInvoiceFlagsDraft: (flags: Record<number, 'Y' | 'N'>) => void;
  };
}

// V1 parity: deleted documents stay listed (greyed, "DELETED - " prefix,
// sorted last) so users can see what was removed and by whom.
const DEFAULT_FILTERS: ProDocumentFilters = { revision: 'all' };

function ConfirmDeleteDialog({
  doc,
  busy,
  onConfirm,
  onCancel,
}: {
  doc: ProDocumentRow;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (busy) return;
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="docs-delete-confirm-title"
        className="w-full max-w-md rounded-lg bg-panel shadow-xl"
      >
        <ModalHeader
          title="Delete Document"
          titleId="docs-delete-confirm-title"
          onClose={busy ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
        />
        <div className="px-4 py-4 text-sm text-gray-700 dark:text-gray-200 sm:px-6">
          Are you sure you want to delete{' '}
          <span className="font-semibold">{doc.filename}</span>? This action cannot
          be undone.
          {doc.source === 'invoice' && (
            // One invoice PDF can serve several pros (a master invoice resolves to
            // every pro on it), and deleting removes it from all of them. Say so —
            // the clerk is standing on one pro and cannot see the others.
            <p className="mt-2 text-amber-700 dark:text-amber-400">
              This is a generated invoice document. It will be removed from every PRO
              on its invoice, not just this one.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-theme-border-primary px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-80"
          >
            {busy && (
              <span
                className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
                aria-hidden="true"
              />
            )}
            {busy ? 'Deleting…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Identifies the in-flight Invoice toggles so they can be counted — see the mutation. */
const TOGGLE_INVOICE_KEY = ['proDocs', 'toggle-invoice'] as const;

export default function DocsTab({ pronumber, editing }: DocsTabProps) {
  const [filters, setFilters] = useState<ProDocumentFilters>(DEFAULT_FILTERS);
  // Edit dialog state: list of rows to show in the dropdown + which to focus.
  // Empty list = dialog closed.
  const [editingRows, setEditingRows] = useState<ProDocumentRow[]>([]);
  const [editingFocus, setEditingFocus] = useState<number | null>(null);
  const [historyFor, setHistoryFor] = useState<ProDocumentRow | null>(null);
  const [previewing, setPreviewing] = useState<ProDocumentRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProDocumentRow | null>(null);
  // Change All: the doc whose "Copy to all" was clicked (opens the review modal).
  const { enabled: changeAllEnabled, targetPros } = useChangeAll();
  const [copyDoc, setCopyDoc] = useState<ProDocumentRow | null>(null);

  const queryClient = useQueryClient();

  const docsQuery = useQuery({
    queryKey: ['proDocs', pronumber, filters],
    queryFn: () => proDocumentsService.list(pronumber, filters),
    // A few seconds of freshness, NOT zero. At staleTime 0 every window-focus event
    // refetched the list immediately, which on some platforms fires while a native
    // <select> menu is open — the grid then re-renders underneath the open menu. This
    // does not weaken the save path: invalidateQueries refetches an active query
    // whatever its staleTime, so a toggle still refreshes the list straight away.
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

  const docTypesQuery = useQuery({
    queryKey: ['docTypes'],
    queryFn: () => proDocumentsService.documentTypes(),
    staleTime: 5 * 60 * 1000,
  });

  const driversQuery = useQuery({
    queryKey: ['proDrivers', pronumber],
    queryFn: () => proDocumentsService.driversForPro(pronumber),
  });

  const historyQuery = useQuery({
    queryKey: ['proDocsHistory', pronumber, historyFor?.map_recno],
    queryFn: () => proDocumentsService.history(pronumber, historyFor!.map_recno),
    enabled: !!historyFor,
  });

  const invalidateDocs = () =>
    queryClient.invalidateQueries({ queryKey: ['proDocs', pronumber] });

  const uploadMutation = useMutation({
    mutationFn: ({ file, meta }: { file: File; meta: ProDocumentUpdate }) =>
      proDocumentsService.upload(pronumber, file, meta),
    onSuccess: () => invalidateDocs(),
  });

  const editMutation = useMutation({
    mutationFn: ({
      mapRecno,
      body,
    }: {
      mapRecno: number;
      body: ProDocumentUpdate;
    }) => proDocumentsService.edit(pronumber, mapRecno, body),
    onSuccess: () => invalidateDocs(),
  });

  // `source` rides along: cmsinvoice_map and cms_map recnos share the integer space,
  // so a bare recno names two different rows. An invoice delete is additionally gated
  // on `invoice-doc-delete`, resolved again server-side.
  const deleteMutation = useMutation({
    mutationFn: ({ mapRecno, source }: { mapRecno: number; source: 'cms' | 'invoice' }) =>
      proDocumentsService.remove(pronumber, mapRecno, source),
    onSuccess: () => invalidateDocs(),
  });

  /**
   * The Invoice (forinvoice) toggle. Saves immediately — there is no order Save to
   * fold it into — so it is optimistic, and the two rules below are what make a
   * click STICK.
   *
   * 1. WRITE TO EVERY CACHED FILTER, not just the active one. The old code built
   *    `['proDocs', pronumber, filters]` by hand and patched that single entry.
   *    Any other filter combination the user had already visited kept the stale
   *    value, so toggling a row and then changing the filter showed the change
   *    reverting. `setQueriesData` on the PREFIX patches them all.
   *
   * 2. ONLY REFETCH WHEN THIS IS THE LAST TOGGLE IN FLIGHT. `onSettled` used to
   *    invalidate unconditionally, which is the documented way to lose a
   *    concurrent optimistic update: toggle row A, toggle row B a moment later,
   *    and A's refetch can return BEFORE B's PUT commits — so the server answer
   *    overwrites B's optimistic value and B visibly snaps back to its old
   *    setting. That is the "sometimes does not take the value you clicked"
   *    report. `isMutating` counts this one too, hence `<= 1`.
   */
  const toggleInvoiceMutation = useMutation({
    mutationKey: TOGGLE_INVOICE_KEY,
    mutationFn: ({
      mapRecno,
      value,
    }: {
      mapRecno: number;
      value: 'Y' | 'N';
    }) => proDocumentsService.edit(pronumber, mapRecno, { forinvoice: value }),
    onMutate: async ({ mapRecno, value }) => {
      await queryClient.cancelQueries({ queryKey: ['proDocs', pronumber] });
      const prev = queryClient.getQueriesData<ProDocumentRow[]>({
        queryKey: ['proDocs', pronumber],
      });
      // Match on source too: invoice-doc rows share the recno space with
      // cms_map rows but are read-only (never toggled).
      queryClient.setQueriesData<ProDocumentRow[]>(
        { queryKey: ['proDocs', pronumber] },
        (old) =>
          (old || []).map((r) =>
            r.map_recno === mapRecno && r.source !== 'invoice'
              ? { ...r, forinvoice: value }
              : r,
          ),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.prev ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: TOGGLE_INVOICE_KEY }) <= 1) {
        invalidateDocs();
      }
    },
  });

  const handleUpload = async (
    file: File,
    meta: ProDocumentUpdate,
  ): Promise<ProDocumentRow | null> => {
    const res = await uploadMutation.mutateAsync({ file, meta });
    return res.document;
  };

  // Called by DocsUploadForm once per drop/pick after every file in the batch
  // has finished uploading. Open the edit dialog with the whole batch.
  const handleUploaded = (rows: ProDocumentRow[]) => {
    if (rows.length === 0) return;
    setEditingRows(rows);
    setEditingFocus(rows[0]?.map_recno ?? null);
  };

  const handleEditSave = async (mapRecno: number, update: ProDocumentUpdate) => {
    await editMutation.mutateAsync({ mapRecno, body: update });
  };

  const closeEditDialog = () => {
    setEditingRows([]);
    setEditingFocus(null);
  };

  const openEditFor = (row: ProDocumentRow) => {
    setEditingRows([row]);
    setEditingFocus(row.map_recno);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    await deleteMutation.mutateAsync({
      mapRecno: confirmDelete.map_recno,
      source: confirmDelete.source === 'invoice' ? 'invoice' : 'cms',
    });
    setConfirmDelete(null);
  };

  /**
   * Stage the Invoice flag, or write it immediately when there is no order save to
   * fold into.
   *
   * Staging drops back to NO staged entry when the user returns a row to the value the
   * server already holds — otherwise toggling Y then N would leave the order dirty with
   * nothing to save, and the navigate-away warning would fire over a no-op.
   */
  const handleToggleInvoice = (row: ProDocumentRow, value: 'Y' | 'N') => {
    if (!editing) {
      toggleInvoiceMutation.mutate({ mapRecno: row.map_recno, value });
      return;
    }
    const next = { ...editing.docInvoiceFlagsDraft };
    const saved = serverFlags.get(row.map_recno) ?? row.forinvoice;
    if (value === saved) delete next[row.map_recno];
    else next[row.map_recno] = value;
    editing.setDocInvoiceFlagsDraft(next);
  };

  /**
   * V1's set-all checkbox (togglebyqueryall). Stages the whole visible set in ONE
   * update rather than looping handleToggleInvoice, which would rebuild the draft from
   * a stale `docInvoiceFlagsDraft` on every iteration and keep only the last row.
   *
   * Offered only in the staged mode — see DocsTable's onSetAllInvoice. Rows already at
   * the target value drop out of the draft the same way a single toggle does, so
   * ticking and un-ticking leaves the order clean.
   */
  const handleSetAllInvoice = (targetRows: ProDocumentRow[], value: 'Y' | 'N') => {
    if (!editing) return;
    const next = { ...editing.docInvoiceFlagsDraft };
    for (const row of targetRows) {
      const saved = serverFlags.get(row.map_recno) ?? row.forinvoice;
      if (value === saved) delete next[row.map_recno];
      else next[row.map_recno] = value;
    }
    editing.setDocInvoiceFlagsDraft(next);
  };

  // What the SERVER holds, so a staged value can be compared against it (and dropped
  // when the user lands back on it).
  const serverFlags = useMemo(
    () => new Map((docsQuery.data || []).map((r) => [r.map_recno, r.forinvoice])),
    [docsQuery.data],
  );

  // The grid renders staged-over-server. Staging lives in the editing hook rather than
  // here precisely so it survives this tab unmounting on a tab switch.
  // Lenient: DocsTab renders in test trees and previews with no permissions provider.
  const permissionsMode = useTryPermissionsMode()?.permissionsMode ?? false;

  const visibleRows = useMemo(() => {
    const staged = editing?.docInvoiceFlagsDraft;
    const rows = docsQuery.data || [];
    if (!staged || Object.keys(staged).length === 0) return rows;
    return rows.map((r) =>
      staged[r.map_recno] != null && r.source !== 'invoice'
        ? { ...r, forinvoice: staged[r.map_recno] }
        : r,
    );
  }, [docsQuery.data, editing?.docInvoiceFlagsDraft]);

  return (
    <div className="docs-tab flex h-full min-h-0 flex-col gap-3 p-3">
      <div className="shrink-0">
        <DocsUploadForm
          pronumber={pronumber}
          onUpload={handleUpload}
          onUploaded={handleUploaded}
        />
      </div>
      <div className="shrink-0">
        <DocsFilterBar
          value={filters}
          onChange={setFilters}
          docTypes={docTypesQuery.data || []}
        />
      </div>
      {/* Permissions-mode ONLY anchor for `invoice-doc-delete`. The capability itself
          lives on each invoice row's trash icon, but those containers pass
          showHandle={false} (N rows would sprout N cogs) and a pro with no invoice PDFs
          renders none at all — so without this there is nowhere to click the cog and
          grant another group. Same componentId, so the popover here configures the row
          buttons. Renders nothing outside permissions mode, for anyone. */}
      {permissionsMode && (
        <div className="shrink-0">
          <PermissionsContainer
            componentId={INVOICE_DOC_DELETE_PERMISSION_ID}
            componentName={INVOICE_DOC_DELETE_PERMISSION_NAME}
            defaultHidden
            className="inline-flex"
          >
            <span className="inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-2 py-1 text-[0.6875rem] text-slate-600 dark:border-gray-600 dark:text-gray-300">
              <TrashIcon className="h-3.5 w-3.5" />
              Delete Invoice Document
            </span>
          </PermissionsContainer>
        </div>
      )}
      <div className="min-h-0 flex-1">
        <DocsTable
          rows={visibleRows}
          onSetAllInvoice={editing ? handleSetAllInvoice : undefined}
          loading={docsQuery.isLoading}
          onEdit={openEditFor}
          onHistory={setHistoryFor}
          onDelete={setConfirmDelete}
          onPreview={setPreviewing}
          onToggleInvoice={handleToggleInvoice}
          onCopyToAll={changeAllEnabled ? setCopyDoc : undefined}
        />
      </div>
      {copyDoc && (
        <ChangeAllReviewModal
          isOpen
          pros={targetPros}
          onClose={() => setCopyDoc(null)}
          title="Copy Document to All"
          summary={
            <div className="text-xs text-slate-600 dark:text-gray-300">
              Copying <span className="font-medium">{copyDoc.documenttype || 'document'}</span>
              {copyDoc.filename ? ` (${copyDoc.filename})` : ''} to every target pro.
            </div>
          }
          previewFn={(pros, signal) =>
            proDocumentsService.copyToAllPreview(pronumber, copyDoc.fileinfo_recno, pros, signal)}
          applyFn={(chunk, nonce) =>
            proDocumentsService.copyToAllApply(pronumber, copyDoc.fileinfo_recno, chunk, nonce)}
        />
      )}
      <DocsEditDialog
        rows={editingRows}
        initialMapRecno={editingFocus}
        docTypes={docTypesQuery.data || []}
        drivers={driversQuery.data || []}
        onSave={handleEditSave}
        onClose={closeEditDialog}
      />
      <DocsHistoryDrawer
        open={!!historyFor}
        title={historyFor?.filename}
        entries={historyQuery.data || []}
        loading={historyQuery.isLoading}
        onClose={() => setHistoryFor(null)}
      />
      {previewing && (
        <DocPreviewLightbox
          pronumber={pronumber}
          mapRecno={previewing.map_recno}
          source={previewing.source}
          filename={previewing.filename}
          filetype={previewing.filetype}
          onClose={() => setPreviewing(null)}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteDialog
          doc={confirmDelete}
          busy={deleteMutation.isPending}
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
